import { defineStore } from 'pinia';
import { Booking, BookingStatus } from '@/model/booking';
import { Customer } from '@/model/customer';
import { Vendor } from '@/model/vendor';
import { EventService, Service } from '@/model/service';
import { addMonths, addWeeks, format, subDays } from 'date-fns';
import { API_DATE_FORMAT } from '@/constants';
import {
  apiDatesBetween,
  firstDayOfMonth,
  isDayWithin,
  monthGrid,
  notBeforeToday,
  selectedMonthOf,
} from '@/helpers/date';
import cloneDeep from 'lodash-es/cloneDeep';
import bookingService from '@/services/booking.service';
import * as Sentry from '@sentry/vue';
import { Location } from '@/model/location';
import { useServicesStore } from '@/store/services-store';
import orderBy from 'lodash-es/orderBy';
import { AlertType, useUiStore } from '@/store/ui-store';
import { MathHelper } from '@/helpers/math-helper';
import { Region } from '@/model/region';
import type { CalendarSelectedMonth } from '@/model/calendar-selected-month';
import { useCustomersStore } from '@/store/customers-store';
import bookingsService from '@/services/booking.service';

export interface AvailableBookingsForDate {
  date: string;
  bookings: Booking[];
  loading: boolean;
}

export interface AvailabilityForDate {
  date: string;
  slots: number;
  loading: boolean;
}

interface BookingRequest {
  bookingInEdition: Booking;
  customer: Customer;
  vendor: Vendor;
  fetching: boolean;
  fetchingHistory: boolean;
  failure: boolean;
  saving: boolean;
  closing: boolean;
  status: BookingStatus;
  savingMessage: string;
  comment: string | null;
  privateComment: string | null;
  currentStep: number;
  location: Location;
  region: Region;
  services: EventService[];
  favoriteEmployeeId: number | null;
  favoriteEmployeeOnly: boolean;
  selectedBooking: Booking | null;
  acceptedLegalConditionsIds: number[];
  validationErrors: object;
}

const initialBookingObject: BookingRequest = {
  bookingInEdition: {} as Booking,
  customer: null as Customer,
  vendor: {} as Vendor,
  fetching: false as boolean,
  fetchingHistory: false as boolean,
  failure: false as boolean,
  saving: false as boolean,
  closing: false as boolean,
  status: BookingStatus.OPEN,
  savingMessage: null as string,
  comment: null,
  privateComment: null,
  currentStep: 1 as number,
  location: {} as Location,
  region: {} as Region,
  services: [] as EventService[],
  favoriteEmployeeId: null as number | null,
  favoriteEmployeeOnly: false as boolean,
  selectedBooking: {} as Booking | null,
  acceptedLegalConditionsIds: [] as number[],
  validationErrors: {},
};

export const useBookingsStore = defineStore('bookings', {
  state: () => ({
    date: null as string | null,
    month: {
      ...selectedMonthOf(new Date()),
    } as CalendarSelectedMonth,
    availableBookings: [] as AvailableBookingsForDate[],
    availabilities: [] as AvailabilityForDate[],
    booking: cloneDeep(initialBookingObject) as BookingRequest,
    sendBookingConfirmation: true as boolean,
    public: false as boolean,
  }),
  getters: {
    getBookingForApi(state): Booking {
      const booking: Booking = {
        customer_id: state.booking.customer?.id,
        comment: state.booking.comment,
        private_comment: state.booking.privateComment,
        events: state.booking.services?.map((service) => {
          return {
            id: service.event_id ?? null,
            service_id: service.id,
            employee_id: service.employee_id,
            workplace_id: service.workplace_id,
            location_id: state.booking.location.id,
            manual: true,
            start: null,
            customer_start: null,
            customer_end: null,
            end: null,
            effective_duration: service.duration,
            vendor_id: state.booking.vendor?.id,
          };
        }),
      };

      if (state.booking.bookingInEdition.id) {
        booking.id = state.booking.bookingInEdition.id;
        booking.events = booking.events.map((event) => {
          return {
            ...event,
            booking_id: state.booking.bookingInEdition.id,
          };
        });
      }
      return booking;
    },
    getAvailabilityForDate:
      (state) =>
      (date: string): AvailabilityForDate => {
        return state.availabilities.find(
          (entityForDay: AvailabilityForDate) => {
            return entityForDay.date === date;
          },
        );
      },
    isCurrentMonthLoadingAvailabilities(state): boolean {
      const firstDate = monthGrid(firstDayOfMonth(state.month)).start;

      return apiDatesBetween(
        firstDate,
        subDays(addWeeks(firstDate, 6), 1),
      ).some((date) => this.getAvailabilityForDate(date)?.loading);
    },
    getAvailableBookingsForDate:
      (state) =>
      (date: string): AvailableBookingsForDate => {
        return state.availableBookings.find(
          (entityForDay: AvailableBookingsForDate) => {
            return entityForDay.date === date;
          },
        );
      },
  },
  actions: {
    _bookingExceptionHandler(exception) {
      this.booking.failure = true;
      const uiStore = useUiStore();
      uiStore.alert(exception.message, AlertType.DANGER, 10, exception.errors);
      if (exception.errors !== undefined) {
        console.log(exception.errors);
        this.booking.validationErrors = exception.errors;
      }
      this.booking.saving = false;
    },
    async _getUpdatedBooking(id, customerId) {
      const customersStore = useCustomersStore();
      const booking = await customersStore.findBookingById(id, customerId);
      customersStore.bookings.update = true;
      customersStore.bookings.currentEntity = booking ?? ({} as Booking);
      return booking;
    },
    async addBooking(addBookingDto: Booking): Promise<Booking> {
      this.booking.saving = true;
      this.booking.failure = false;
      try {
        await bookingService.verify(addBookingDto.customer_id, addBookingDto);
        const response = await bookingService.save(
          addBookingDto.customer_id,
          addBookingDto,
        );
        const booking = await this._getUpdatedBooking(
          response.id,
          addBookingDto.customer_id,
        );
        this.booking.saving = false;
        return booking;
      } catch (exception) {
        this._bookingExceptionHandler(exception);
      }
    },
    async editBooking(
      addBookingDto: Booking,
      sendBookingConfirmation: boolean,
    ): Promise<Booking> {
      try {
        this.booking.saving = true;
        this.booking.failure = false;
        const response = await bookingService.edit(
          addBookingDto.id,
          addBookingDto,
          sendBookingConfirmation,
        );
        const booking = await this._getUpdatedBooking(
          response.id,
          addBookingDto.customer_id,
        );
        this.booking.saving = false;
        return booking;
      } catch (exception) {
        this._bookingExceptionHandler(exception);
      }
    },
    async closeBooking(addBookingDto: Booking) {
      try {
        this.booking.saving = true;
        this.booking.failure = false;
        if (addBookingDto.id === undefined) {
          const response = await bookingService.addAndCloseExpress(
            addBookingDto.customer_id,
            addBookingDto,
          );
          addBookingDto.id = response.id;
        } else {
          await bookingService.close(addBookingDto.id, addBookingDto);
        }
        const booking = await this._getUpdatedBooking(
          addBookingDto.id,
          addBookingDto.customer_id,
        );
        this.booking.saving = false;
        return booking;
      } catch (exception) {
        this._bookingExceptionHandler(exception);
      }
    },
    setStep(stepNumber: number) {
      this.booking.currentStep = stepNumber;
    },
    async nextStep() {
      this.booking.currentStep++;
      if (
        ((!this.public && this.booking.currentStep === 4) ||
          (this.public && this.booking.currentStep === 3)) &&
        this.availabilities.length === 0
      ) {
        await this.findAvailabilitiesForUpcomingMonths();
        if (Object.keys(this.booking.bookingInEdition).length > 0) {
          this.findAvailableBookingsForCurrentDate();
        }
      }
    },
    previousStep() {
      this.booking.currentStep--;
      this.booking.acceptedLegalConditionsIds = [];
    },
    setBookingInEdition(booking: Booking) {
      this.booking.bookingInEdition = Object.assign(
        {},
        this.booking.bookingInEdition,
        booking,
      );
    },
    selectCustomer(customer: Customer) {
      this.booking.customer = customer;
    },
    setVendorId(vendorId: number, checkLocationVendor: boolean = true) {
      this.booking.vendor = Object.assign({}, this.booking.vendor, {
        id: vendorId,
      });
      if (
        checkLocationVendor &&
        this.booking.location.vendor_id !== this.booking.vendor.id
      ) {
        this.resetBookingAddForm();
      }
    },
    setVendorSlug(slug: string) {
      this.booking.vendor = Object.assign({}, this.booking.vendor, {
        slug,
      });
    },
    setBookingAsPublic(isPublic: boolean) {
      this.public = isPublic;
    },
    setDate(date: string | null) {
      this.date = date;
    },
    setMonth(month: CalendarSelectedMonth) {
      this.month = month;
    },
    setEmployeeId(employeeId?: number | string, reset = true) {
      if (employeeId) {
        this.setEmployeeOnly(true, false);
      } else {
        this.setEmployeeOnly(false, false);
      }
      this.$patch({
        booking: {
          favoriteEmployeeId: parseInt(employeeId as string, 10),
          selectedBooking: {},
        },
      });
      if (reset) {
        this.resetAvailabilities();
        this.reloadCalendarData();
      }
    },
    setEmployeeOnly(employeeOnly: boolean, fetch: boolean) {
      this.$patch({
        booking: {
          favoriteEmployeeOnly: employeeOnly,
          selectedBooking: {},
        },
      });
      if (fetch) {
        this.resetAvailabilities();
        this.reloadCalendarData();
      }
    },
    setServiceDuration(
      serviceToEdit: Service,
      duration: number,
      resetSelectedBooking = true,
    ) {
      this.booking = Object.assign({}, this.booking, {
        services: this.booking.services.map((service) => {
          if (service.id === serviceToEdit.id) {
            return {
              ...service,
              duration,
            };
          } else {
            return service;
          }
        }),
      });

      if (resetSelectedBooking) {
        this.resetSelectedBooking();
      }
    },
    setServiceEmployee(
      serviceToEdit: Service,
      employeeId: number,
      resetSelectedBooking = true,
    ) {
      this.booking = {
        ...this.booking,
        services: this.booking.services.map((service) => {
          if (service.id === serviceToEdit.id) {
            return {
              ...service,
              employee_id: employeeId,
            };
          } else {
            return service;
          }
        }),
      };

      if (resetSelectedBooking) {
        this.resetSelectedBooking();
      }
    },
    setServiceWorkplace(
      serviceToEdit: Service,
      workplaceId: number,
      resetSelectedBooking = true,
    ) {
      this.booking = {
        ...this.booking,
        services: this.booking.services.map((service) => {
          if (service.id === serviceToEdit.id) {
            return {
              ...service,
              workplace_id: workplaceId,
            };
          } else {
            return service;
          }
        }),
      };

      if (resetSelectedBooking) {
        this.resetSelectedBooking();
      }
    },
    addService(service: Service) {
      this.booking.services = orderBy(
        this.booking.services.concat(service),
        'performed_order',
      );
      this.resetSelectedBooking();
    },
    removeService(service: Service, resetAndReload = true) {
      this.booking.services = this.booking.services.filter((currentService) => {
        return currentService.id !== service.id;
      });
      if (resetAndReload) {
        this.resetSelectedBooking();
        this.resetAvailabilities();
        if (this.booking.services.length > 0) {
          this.reloadCalendarData();
        }
      }
    },
    async fetchServicesHistoryForCustomer() {
      const customerId = this.booking.customer?.id;
      if (!customerId) {
        return;
      }
      this.booking.fetchingHistory = true;

      const variableDurationServices = this.booking.services.filter(
        (service) => !service.fixed_duration,
      );
      try {
        const servicesHistory = await Promise.all(
          variableDurationServices.map((service) =>
            bookingsService.getServiceHistoryForCustomer(
              service.id,
              customerId,
            ),
          ),
        );
        for (let i = 0; i < variableDurationServices.length; i++) {
          this.setServiceHistory(
            variableDurationServices[i],
            servicesHistory[i],
          );
        }
      } catch (e) {
        console.error(e.message);
        Sentry.captureException(e);
      }

      this.booking.fetchingHistory = false;
    },
    setServiceHistory(
      serviceToEdit: Service,
      history: {
        id: number;
        employee_id: number;
        effective_duration: number;
      }[],
    ) {
      const servicesStore = useServicesStore();
      this.booking = Object.assign({}, this.booking, {
        services: this.booking.services.map((service) => {
          let medianDurationForEmployee = servicesStore.getById(
            service.id,
          ).duration;
          if (
            this.booking.favoriteEmployeeId &&
            this.booking.favoriteEmployeeOnly
          ) {
            history = history.filter(
              (event) => event.employee_id === this.booking.favoriteEmployeeId,
            );
          }
          if (history.length > 0) {
            medianDurationForEmployee = MathHelper.getMedian(
              history.map((history) => history.effective_duration),
            );
            if (medianDurationForEmployee) {
              medianDurationForEmployee =
                Math.ceil(medianDurationForEmployee / 5) * 5;
            }
          }

          if (service.id === serviceToEdit.id) {
            return {
              ...service,
              duration: medianDurationForEmployee,
              history,
              average_duration: medianDurationForEmployee,
            };
          } else {
            return service;
          }
        }),
      });
    },
    selectBooking(booking?: Booking) {
      if (booking) {
        this.booking.selectedBooking = booking;
      } else {
        this.booking.selectedBooking = null;
      }
    },
    resetSelectedBooking() {
      this.booking.selectedBooking = null;
    },
    resetBookingAddForm() {
      this.booking = {
        ...cloneDeep(initialBookingObject),
        vendor: this.booking.vendor,
      };
      this.setDate(null);
      this.setStep(1);
      this.month = selectedMonthOf(new Date());
      this.resetAvailabilities();
    },
    setAcceptedLegalConditions(legalConditionIds: number[]) {
      this.booking.acceptedLegalConditionsIds = legalConditionIds;
    },
    toggleAcceptedLegalCondition(legalConditionId: number) {
      const index =
        this.booking.acceptedLegalConditionsIds.indexOf(legalConditionId);
      if (index === -1) {
        this.booking.acceptedLegalConditionsIds.push(legalConditionId);
      } else {
        this.booking.acceptedLegalConditionsIds.splice(index, 1);
      }
    },
    setLocation(location: Location) {
      if (this.booking.location?.id !== location.id) {
        if (
          Object.keys(this.booking.location).length !== 0 &&
          this.booking.location?.vendor?.id !== location.vendor_id
        ) {
          this.resetBookingAddForm();
        } else {
          this.resetSelectedBooking();
          this.resetAvailabilities();
        }
        this.booking.location = location;
        const servicesStore = useServicesStore();
        if (location.vendor.workplaces_enabled) {
          const availableServicesAtLocation =
            servicesStore.filterServicesForChosenLocation(location.id);
          const services: Service[] = cloneDeep(this.booking.services);
          services.forEach((selectedService) => {
            const serviceIsAvailableAtLocation =
              availableServicesAtLocation.find((service) => {
                return service.id === selectedService.id;
              });
            if (!serviceIsAvailableAtLocation) {
              this.removeService(selectedService, false);
            }
          });
          if (services.length < this.booking.services.length) {
            this.reloadCalendarData();
          }
        }
      }
    },
    resetAvailabilities() {
      this.$patch({
        availabilities: [],
      });
      this.$patch({
        availableBookings: [],
      });
    },
    async reloadCalendarData() {
      await this.findAvailabilitiesForUpcomingMonths();
      if (this.date) {
        this.findAvailableBookingsForCurrentDate();
      }
    },
    async findAvailabilitiesForUpcomingMonths(monthsCount = 2, force = false) {
      for (let i = 0; i < monthsCount; i++) {
        const start = monthGrid(
          addMonths(firstDayOfMonth(this.month), i),
        ).start;
        let date_from: string = notBeforeToday(start);
        let date_to: string = format(
          subDays(addWeeks(start, 6), 1),
          API_DATE_FORMAT,
        );

        if (!force) {
          const datesToFind = apiDatesBetween(date_from, date_to).filter(
            (date) =>
              !this.availabilities.some(
                (entityForDay: AvailabilityForDate) =>
                  entityForDay.date === date,
              ),
          );
          date_from = datesToFind[0];
          date_to = datesToFind[datesToFind.length - 1];
        }
        if (date_from && date_to) {
          await this._findAvailabilities(date_from, date_to);
        }
      }
    },
    _setAvailabilitiesLoading(
      date_from: string,
      date_to: string,
      loading: boolean,
    ) {
      const availabilitiesUpdated: AvailabilityForDate[] = this.availabilities;
      apiDatesBetween(date_from, date_to).forEach((date) => {
        const availabilityForDate: AvailabilityForDate | null =
          this.getAvailabilityForDate(date);
        if (availabilityForDate) {
          availabilityForDate.loading = loading;
        } else {
          availabilitiesUpdated.push({
            loading,
            date,
            slots: null,
          });
        }
      });
      this.$patch({
        availabilities: availabilitiesUpdated,
      });
    },
    async _findAvailabilities(date_from: string, date_to: string) {
      try {
        this._setAvailabilitiesLoading(date_from, date_to, true);
        if (this.booking.services.length > 0) {
          const availabilitiesFromAPI = await bookingService.findAvailabilities(
            {
              date_from: notBeforeToday(date_from),
              date_to,
              vendor_id: this.booking.vendor.id,
              location_id: this.booking.location.id,
              services: this.booking.services,
              employee_id: this.booking.favoriteEmployeeId ?? null,
              favorite_employee_only:
                this.booking.favoriteEmployeeOnly ?? false,
              exclude_events_ids:
                Object.keys(this.booking.bookingInEdition).length === 0
                  ? []
                  : this.booking.bookingInEdition.events.map((event) => {
                      return event.id;
                    }),
            },
          );

          if (
            this.date !== null &&
            typeof availabilitiesFromAPI[this.date] !== 'undefined' &&
            availabilitiesFromAPI[this.date] === 0
          ) {
            this.setDate(null);
          }

          this.$patch({
            availabilities: this.availabilities.map(
              (availability: AvailabilityForDate) => {
                if (isDayWithin(availability.date, date_from, date_to)) {
                  return {
                    ...availability,
                    slots: availabilitiesFromAPI[availability.date],
                  };
                } else {
                  return availability;
                }
              },
            ),
          });
        }
      } catch (exception) {
        console.error(exception);
        Sentry.captureException(exception);
        const uiStore = useUiStore();
        uiStore.alert(exception.message);
      } finally {
        this._setAvailabilitiesLoading(date_from, date_to, false);
      }
    },
    _setAvailableBookingsLoading(date: string, loading: boolean) {
      const availableBookingsForDate: AvailableBookingsForDate =
        this.getAvailableBookingsForDate(this.date);
      if (availableBookingsForDate) {
        this.$patch({
          availableBookings: this.availableBookings.map(
            (availableBookingsForDate: AvailableBookingsForDate) => {
              if (availableBookingsForDate.date === date) {
                return {
                  ...availableBookingsForDate,
                  loading,
                };
              } else {
                return availableBookingsForDate;
              }
            },
          ),
        });
      } else {
        this.$patch({
          availableBookings: this.availableBookings.concat([
            { date, loading, bookings: [] },
          ]),
        });
      }
    },
    async findAvailableBookingsForCurrentDate() {
      if (!this.date) {
        return;
      }

      const availableBookingsForDate: AvailableBookingsForDate =
        this.getAvailableBookingsForDate(this.date);
      if (!availableBookingsForDate) {
        this._setAvailableBookingsLoading(this.date, true);
      }

      const uiStore = useUiStore();
      bookingService
        .getTimeSlotsForDate(
          this.booking.location.id,
          this.booking.services,
          this.date,
          this.booking.favoriteEmployeeId,
          this.booking.favoriteEmployeeOnly,
          Object.keys(this.booking.bookingInEdition).length === 0
            ? []
            : this.booking.bookingInEdition.events.map((event) => {
                return event.id;
              }),
          this.booking.vendor.id,
        )
        .then((bookings) => {
          this._setAvailableBookings(bookings, this.date);
          if (bookings.length === 0) {
            this.setDate(null);
          }
          this._setAvailableBookingsLoading(this.date, false);
        })
        .catch((exception) => {
          console.error(exception);
          Sentry.captureException(exception);
          uiStore.alert(exception.message);
          this._setAvailableBookingsLoading(this.date, false);
        });
    },
    _setAvailableBookings(bookings: Booking[], date: string) {
      this.$patch({
        availableBookings: this.availableBookings.map(
          (availableBookingsPerDate: AvailableBookingsForDate) => {
            if (availableBookingsPerDate.date === date) {
              return {
                ...availableBookingsPerDate,
                bookings,
              };
            } else {
              return availableBookingsPerDate;
            }
          },
        ),
      });
    },
    setComment(comment: string) {
      if (
        this.booking.selectedBooking &&
        Object.keys(this.booking.selectedBooking).length > 0
      ) {
        this.booking.selectedBooking.comment = comment;
      } else {
        this.booking.comment = comment;
      }
    },
    setPrivateComment(comment: string) {
      if (
        this.booking.selectedBooking &&
        Object.keys(this.booking.selectedBooking).length > 0
      ) {
        this.booking.selectedBooking.private_comment = comment;
      } else {
        this.booking.privateComment = comment;
      }
    },
    setSendBookingConfirmation(value: boolean) {
      this.sendBookingConfirmation = value;
    },
    async save() {
      const uiStore = useUiStore();
      this.booking.saving = true;
      let response: { data: string; message: string };
      try {
        if (Object.keys(this.booking.bookingInEdition).length === 0) {
          response = await bookingService.save(this.booking.customer.id, {
            ...this.booking.selectedBooking,
            acceptedLegalConditionsIds: this.booking.acceptedLegalConditionsIds,
          });
        } else {
          this.booking.selectedBooking = {
            ...this.booking.selectedBooking,
            customer_id: this.booking.bookingInEdition.customer_id,
          };
          response = await bookingService.edit(
            this.booking.bookingInEdition.id,
            this.booking.selectedBooking,
            this.sendBookingConfirmation,
          );
        }
        this.booking.saving = false;
        this.booking.savingMessage = response.message ?? '';

        return true;
      } catch (exception) {
        uiStore.alertValidationError(exception);
        this.booking.saving = false;
        return false;
      }
    },
  },
});
